import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';

// Blog posts data with full content for each category
const blogPostsData = {
  'ai-technology': {
    title: 'AI Technology',
    description: 'Explore the cutting-edge AI algorithms and machine learning techniques powering the future of audio processing.',
    posts: [
      {
        id: 1,
        title: "The Future of AI-Powered Audio Processing",
        excerpt: "Discover how machine learning is revolutionizing the way we create, edit, and enhance audio content across industries.",
        content: `Artificial Intelligence is fundamentally transforming audio processing. From noise reduction to voice synthesis, AI models are achieving results that were impossible just a few years ago.

Key advancements include:
• Neural network-based noise suppression that adapts in real-time
• AI-powered mastering that analyzes and optimizes audio automatically  
• Voice cloning technology that can replicate any speaker with minimal samples
• Intelligent audio restoration for damaged or degraded recordings

The implications for content creators, musicians, and enterprises are profound. What once required expensive studio equipment and expert engineers can now be accomplished with AI-driven software in seconds.`,
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
        author: "Sarah Chen",
        date: "Mar 8, 2026",
        readTime: "8 min read",
      },
      {
        id: 2,
        title: "Neural Audio Synthesis: Creating Sounds That Never Existed",
        excerpt: "Dive into the world of generative audio models and their applications in music and sound design.",
        content: `Generative AI models like WaveNet, Jukebox, and AudioLM are opening new frontiers in sound creation. These systems can generate entirely new audio—music, speech, sound effects—from text prompts or learned patterns.

Applications in production:
• AI-composed background music for videos and games
• Procedurally generated sound effects
• Voice synthesis for virtual assistants and characters
• Music continuation and arrangement assistance

The creative possibilities are endless, and we're only scratching the surface of what neural audio synthesis can achieve.`,
        image: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=800&q=80",
        author: "Sarah Chen",
        date: "Feb 25, 2026",
        readTime: "11 min read",
      },
      {
        id: 3,
        title: "Transformer Models in Audio: Beyond Text",
        excerpt: "How attention mechanisms are revolutionizing speech recognition and audio understanding.",
        content: `Transformer architectures, originally designed for text, have proven remarkably effective for audio tasks. Models like Whisper, AudioGPT, and MusicLM demonstrate the power of attention-based approaches.

Breakthrough capabilities:
• Multi-language speech recognition with near-human accuracy
• Audio-to-text transcription that understands context and nuance
• Music generation from text descriptions
• Speaker diarization and emotion detection

These models represent a paradigm shift in how we process and understand audio content.`,
        image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80",
        author: "Dr. Michael Torres",
        date: "Feb 18, 2026",
        readTime: "10 min read",
      },
    ],
  },
  'engineering': {
    title: 'Engineering',
    description: 'Deep dives into the technical architecture, infrastructure, and best practices for building scalable audio systems.',
    posts: [
      {
        id: 1,
        title: "Building Scalable Audio Pipelines with Cloud Infrastructure",
        excerpt: "Learn the best practices for architecting robust audio processing systems that can handle millions of requests.",
        content: `Scaling audio processing infrastructure presents unique challenges. Audio files are large, processing is compute-intensive, and latency requirements are strict.

Architecture patterns that work:
• Event-driven processing with queue-based workloads
• GPU clusters for ML inference at scale
• Content delivery networks for global distribution
• Microservices architecture for independent scaling

We'll walk through how SoundFlowAI processes over 10 million audio files monthly with 99.9% uptime.`,
        image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80",
        author: "Marcus Johnson",
        date: "Mar 6, 2026",
        readTime: "12 min read",
      },
      {
        id: 2,
        title: "Real-Time Audio Transcription: A Technical Deep Dive",
        excerpt: "Explore the algorithms and infrastructure powering instant speech-to-text conversion at scale.",
        content: `Real-time transcription requires sub-100ms latency while maintaining accuracy. This is a formidable engineering challenge that combines ML optimization with distributed systems design.

Technical components:
• Streaming audio chunking and buffering strategies
• Model quantization for faster inference
• WebSocket-based real-time communication
• Fallback mechanisms and error recovery

Our system achieves 95%+ word accuracy with median latency under 80ms.`,
        image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80",
        author: "Alex Rivera",
        date: "Mar 2, 2026",
        readTime: "15 min read",
      },
      {
        id: 3,
        title: "Optimizing Audio ML Models for Edge Deployment",
        excerpt: "Techniques for running sophisticated audio AI on mobile devices and embedded systems.",
        content: `Edge deployment enables offline processing, lower latency, and better privacy. But it requires careful optimization to run complex models on constrained hardware.

Optimization strategies:
• Model pruning and knowledge distillation
• INT8 and FP16 quantization
• TensorFlow Lite and ONNX Runtime optimizations
• Hardware-specific acceleration (NPU, DSP)

We've achieved 10x speedup on mobile devices while maintaining 98% of cloud model accuracy.`,
        image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80",
        author: "Lisa Park",
        date: "Feb 15, 2026",
        readTime: "13 min read",
      },
    ],
  },
  'ethics': {
    title: 'Ethics',
    description: 'Examining the ethical implications, responsibilities, and societal impact of AI audio technology.',
    posts: [
      {
        id: 1,
        title: "Voice Cloning Ethics: Where Do We Draw the Line?",
        excerpt: "An in-depth exploration of the ethical considerations surrounding AI voice synthesis and deepfake audio.",
        content: `Voice cloning technology can create convincing replicas of anyone's voice with just minutes of sample audio. This raises profound ethical questions.

Key concerns:
• Consent and voice ownership rights
• Potential for fraud and impersonation
• Misinformation and deepfake audio
• Impact on voice actors and performers

We believe in responsible AI development with built-in safeguards, consent verification, and watermarking to detect synthetic audio.`,
        image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80",
        author: "Dr. Emily Park",
        date: "Mar 4, 2026",
        readTime: "10 min read",
      },
      {
        id: 2,
        title: "AI Bias in Speech Recognition: Addressing the Gap",
        excerpt: "How training data biases affect speech recognition accuracy across different demographics.",
        content: `Speech recognition systems have historically performed worse for certain accents, dialects, and languages. This creates real-world harm and exclusion.

The bias problem:
• Training data skewed toward dominant demographics
• Lower accuracy for non-native speakers
• Dialect and accent discrimination
• Underrepresentation of global languages

Our commitment to fairness includes diverse training data, bias testing, and continuous improvement for underserved communities.`,
        image: "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&q=80",
        author: "Dr. Emily Park",
        date: "Feb 20, 2026",
        readTime: "9 min read",
      },
      {
        id: 3,
        title: "Transparency in AI Audio: Building Trust Through Openness",
        excerpt: "Why explainability and transparency matter in AI-powered audio tools.",
        content: `Users deserve to understand how AI systems process their audio. Black-box models create distrust and can hide problematic behaviors.

Our transparency principles:
• Clear documentation of AI capabilities and limitations
• Audit trails for all AI-powered modifications
• User control over AI features
• Open research and published methodology

Trust is earned through openness, and we're committed to being transparent about how our AI works.`,
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
        author: "James Wilson",
        date: "Feb 12, 2026",
        readTime: "7 min read",
      },
    ],
  },
  'tutorials': {
    title: 'Tutorials',
    description: 'Step-by-step guides and how-tos for getting the most out of AI audio tools and workflows.',
    posts: [
      {
        id: 1,
        title: "Podcast Production Workflow Automation",
        excerpt: "How leading podcast networks are using AI to streamline their production from recording to distribution.",
        content: `Modern podcast production involves dozens of repetitive tasks. AI automation can handle most of them, freeing creators to focus on content.

Automated workflow stages:
• Real-time transcription during recording
• Automatic filler word and silence removal
• AI-powered audio enhancement and leveling
• Automated show notes and chapter markers
• One-click distribution to all platforms

This guide walks through setting up a fully automated podcast pipeline.`,
        image: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800&q=80",
        author: "Jordan Blake",
        date: "Feb 28, 2026",
        readTime: "7 min read",
      },
      {
        id: 2,
        title: "Getting Started with AI Audio Enhancement",
        excerpt: "A beginner's guide to improving your audio quality with AI-powered tools.",
        content: `Poor audio quality can ruin otherwise great content. AI enhancement tools make professional-quality audio accessible to everyone.

What you'll learn:
• Basic audio problems and how AI solves them
• Setting up your first AI enhancement workflow
• Choosing the right settings for different content types
• Before/after comparisons and best practices

By the end, you'll be producing broadcast-quality audio from any source.`,
        image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80",
        author: "Jordan Blake",
        date: "Feb 21, 2026",
        readTime: "6 min read",
      },
      {
        id: 3,
        title: "Advanced Voice Isolation Techniques",
        excerpt: "Master the art of separating vocals from background noise and music.",
        content: `Voice isolation is crucial for interviews, podcasts, and video production. AI has made this once-difficult task surprisingly simple.

Techniques covered:
• Source separation fundamentals
• Handling challenging audio environments
• Preserving voice quality while removing noise
• Combining multiple isolation passes for best results

Includes downloadable practice files and step-by-step video tutorials.`,
        image: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=800&q=80",
        author: "Sam Richardson",
        date: "Feb 14, 2026",
        readTime: "8 min read",
      },
    ],
  },
  'accessibility': {
    title: 'Accessibility',
    description: 'Making audio content accessible to everyone, including those with hearing impairments and disabilities.',
    posts: [
      {
        id: 1,
        title: "Accessibility in Audio: Making Content Universal",
        excerpt: "Best practices for creating audio content that's accessible to everyone, including those with hearing impairments.",
        content: `Accessible audio content reaches broader audiences and often improves the experience for everyone. It's also increasingly required by law.

Accessibility features to implement:
• Accurate transcripts and captions
• Audio descriptions for visual content
• Clear speech and controlled pacing
• Multiple format options (text, audio, video)
• Compatibility with assistive technologies

This guide covers both technical implementation and content creation best practices.`,
        image: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=800&q=80",
        author: "Maria Santos",
        date: "Feb 22, 2026",
        readTime: "9 min read",
      },
      {
        id: 2,
        title: "AI-Powered Captioning: Speed and Accuracy",
        excerpt: "How modern AI makes real-time captioning viable for live events and streaming.",
        content: `Live captioning has traditionally required expensive human stenographers. AI is democratizing access to real-time captions.

Key capabilities:
• Sub-second latency for live events
• Speaker identification and labeling
• Automatic punctuation and formatting
• Multi-language support and translation

We'll show you how to integrate AI captioning into your streaming workflow.`,
        image: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=800&q=80",
        author: "Maria Santos",
        date: "Feb 16, 2026",
        readTime: "8 min read",
      },
      {
        id: 3,
        title: "Designing Audio Interfaces for All Users",
        excerpt: "Creating audio tools that work for users with different abilities and needs.",
        content: `Inclusive design benefits everyone. Audio tools should be usable by people with visual impairments, motor disabilities, and cognitive differences.

Design principles:
• Keyboard navigation and screen reader support
• High contrast and scalable interfaces
• Clear, consistent interaction patterns
• Flexible output formats and customization

Build tools that empower all creators, regardless of ability.`,
        image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&q=80",
        author: "David Chen",
        date: "Feb 8, 2026",
        readTime: "7 min read",
      },
    ],
  },
  'industry-news': {
    title: 'Industry News',
    description: 'Stay updated with the latest trends, announcements, and developments in the audio technology industry.',
    posts: [
      {
        id: 1,
        title: "The Rise of Spatial Audio in Gaming",
        excerpt: "How 3D audio is transforming the gaming experience and what developers need to know.",
        content: `Spatial audio creates immersive 3D soundscapes that respond to head movement and player position. It's becoming essential for competitive gaming and VR.

Industry trends:
• Major platforms adopting spatial audio standards
• Hardware support for head tracking
• Game engine integration improvements
• Impact on player performance and engagement

We analyze the market and predict where spatial audio is headed next.`,
        image: "https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=800&q=80",
        author: "Chris Taylor",
        date: "Feb 19, 2026",
        readTime: "8 min read",
      },
      {
        id: 2,
        title: "Audio AI Funding Hits Record $2.5B in 2025",
        excerpt: "Venture capital pours into audio AI startups as the market matures.",
        content: `The audio AI sector attracted record investment in 2025, signaling strong confidence in the technology's future.

Market highlights:
• Total funding up 180% year-over-year
• Key acquisitions and exits
• Emerging sectors to watch
• Geographic distribution of investment

We break down what this funding boom means for the industry.`,
        image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
        author: "Chris Taylor",
        date: "Feb 10, 2026",
        readTime: "6 min read",
      },
      {
        id: 3,
        title: "Major Labels Embrace AI Music Tools",
        excerpt: "How the music industry is adapting to AI-powered production and distribution.",
        content: `After initial resistance, major record labels are integrating AI tools into their production workflows and artist development.

Industry shifts:
• AI-assisted mixing and mastering becoming standard
• New artist discovery powered by AI
• Collaborative tools between humans and AI
• Evolving copyright and ownership frameworks

The music industry's relationship with AI is rapidly evolving.`,
        image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80",
        author: "Jessica Lee",
        date: "Feb 5, 2026",
        readTime: "7 min read",
      },
    ],
  },
};

// Category page navbar
const CategoryNavbar = () => {
  const [scrolled, setScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-dark-base/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neon-orange to-orange-600 flex items-center justify-center shadow-[0_0_20px_rgba(253,128,46,0.3)]">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div className="hidden sm:block">
              <span className="font-heading text-xl font-bold text-white">Veritone</span>
              <span className="font-heading text-xl font-bold text-neon-orange">Tech</span>
            </div>
          </Link>

          <div className="flex items-center gap-4">
            <Link to="/#blog" className="text-gray-400 hover:text-white transition-colors text-sm">
              ← Back to Blog
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm hidden sm:block">
              Home
            </Link>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

// Blog post card
const BlogPostCard = ({ post, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.article
      className="bg-dark-deeper rounded-2xl overflow-hidden border border-gray-800 hover:border-neon-orange/50 transition-all duration-300"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <motion.img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
          animate={{ scale: isHovered ? 1.05 : 1 }}
          transition={{ duration: 0.4 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-deeper to-transparent" />
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-heading font-bold text-white mb-3 line-clamp-2">
          {post.title}
        </h3>
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
          {post.excerpt}
        </p>

        {/* Expandable content */}
        <motion.div
          initial={false}
          animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
          className="overflow-hidden"
        >
          <p className="text-gray-300 text-sm mb-4 whitespace-pre-line">
            {post.content}
          </p>
        </motion.div>

        {/* Meta */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <span>{post.author}</span>
          <span>{post.date}</span>
        </div>

        {/* Read more button */}
        <motion.button
          onClick={() => setExpanded(!expanded)}
          className="w-full py-3 bg-neon-orange/10 text-neon-orange font-semibold rounded-lg border border-neon-orange/30 hover:bg-neon-orange hover:text-dark-base transition-all"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {expanded ? 'Show Less' : 'Read Full Article'}
        </motion.button>
      </div>
    </motion.article>
  );
};

// Footer
const CategoryFooter = () => (
  <footer className="bg-dark-deeper border-t border-gray-800 py-8">
    <div className="max-w-7xl mx-auto px-4 text-center">
      <p className="text-gray-500 text-sm">
        © 2026 VeritoneTech. All rights reserved.
      </p>
      <div className="flex justify-center gap-6 mt-4">
        <Link to="/privacy" className="text-gray-400 hover:text-neon-orange text-sm transition-colors">Privacy</Link>
        <Link to="/terms" className="text-gray-400 hover:text-neon-orange text-sm transition-colors">Terms</Link>
        <Link to="/" className="text-gray-400 hover:text-neon-orange text-sm transition-colors">Home</Link>
      </div>
    </div>
  </footer>
);

// Category navigation
const CategoryNav = ({ currentCategory }) => {
  const categories = [
    { slug: 'ai-technology', name: 'AI Technology' },
    { slug: 'engineering', name: 'Engineering' },
    { slug: 'ethics', name: 'Ethics' },
    { slug: 'tutorials', name: 'Tutorials' },
    { slug: 'accessibility', name: 'Accessibility' },
    { slug: 'industry-news', name: 'Industry News' },
  ];

  return (
    <div className="flex flex-wrap justify-center gap-3 mb-12">
      {categories.map((cat) => (
        <Link
          key={cat.slug}
          to={`/blog/${cat.slug}`}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            currentCategory === cat.slug
              ? 'bg-neon-orange text-dark-base shadow-[0_0_20px_rgba(253,128,46,0.4)]'
              : 'bg-dark-deeper text-gray-400 border border-gray-700 hover:border-neon-orange/50'
          }`}
        >
          {cat.name}
        </Link>
      ))}
    </div>
  );
};

// Main component
const BlogCategoryPage = () => {
  const { category } = useParams();
  const categoryData = blogPostsData[category];

  // Handle unknown category
  if (!categoryData) {
    return (
      <div className="min-h-screen bg-dark-base flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-heading font-bold text-white mb-4">Category Not Found</h1>
          <p className="text-gray-400 mb-8">The category you're looking for doesn't exist.</p>
          <Link
            to="/#blog"
            className="px-6 py-3 bg-neon-orange text-dark-base font-bold rounded-full"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-base">
      <CategoryNavbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.span
            className="inline-block px-4 py-1 bg-neon-orange/20 text-neon-orange text-sm font-semibold rounded-full mb-6 border border-neon-orange/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Blog Category
          </motion.span>
          <motion.h1
            className="text-4xl md:text-6xl font-heading font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {categoryData.title}
          </motion.h1>
          <motion.p
            className="text-xl text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {categoryData.description}
          </motion.p>
        </div>
      </section>

      {/* Category navigation */}
      <section className="px-4">
        <div className="max-w-7xl mx-auto">
          <CategoryNav currentCategory={category} />
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categoryData.posts.map((post, index) => (
              <BlogPostCard key={post.id} post={post} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-dark-deeper">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Stay Updated
          </h2>
          <p className="text-gray-400 mb-8">
            Subscribe to our newsletter for the latest articles and industry insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <input
              type="email"
              placeholder="Enter your email"
              className="px-6 py-3 bg-dark-base border border-gray-700 rounded-full text-white placeholder-gray-500 focus:border-neon-orange focus:outline-none"
            />
            <motion.button
              className="px-8 py-3 bg-neon-orange text-dark-base font-bold rounded-full"
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(253,128,46,0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              Subscribe
            </motion.button>
          </div>
        </div>
      </section>

      <CategoryFooter />
    </div>
  );
};

export default BlogCategoryPage;
